import { Metamodel } from '../base/model';
import { IModel, IAttribute, IViolation, IValidationContext } from '../base/annotation';
import { LinkedService } from './linkedservice';
import { format, sanitizeAndFormatString } from '../../shared/utils/string.util';
import { Credential } from '../credential/credential';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { ModelConstants } from '../model-constants';
import { GlobalVariables } from '../../constants/globals';
import { AzureKeyVault } from './key-vault';
import { azureDefaultAuthRefTypes, lakehouseAuthTypes } from '../../interfaces/IAuthenticationType';
import {
    validateAKVLinkedServiceReference,
    validateLinkedServiceProperties
} from '../../shared/utils/linkedservice-validation-util';
import { FormatDatasetV2 } from '../dataset/dataset.v2';
import { Dataset } from '../dataset/dataset';
import { Named } from '../base/named';

export type RootFolderType = 'Files' | 'Tables';

@Metamodel.model({
    name: ModelConstants.lakehouseLinkedService,
    display: [
        format(shellStringResources.ModelName_LinkedServiceTemplate, shellStringResources.Lakehouse_DisplayText),
        format(shellStringResources.ModelName_LinkedServicesTemplate, shellStringResources.Lakehouse_DisplayText)
    ],
    validator: LakehouseLinkedService.validateLakehouseLinkedService
})
export class LakehouseLinkedService extends LinkedService {
    public static readonly MODEL: IModel;
    public static readonly WORKSPACE_ID: IAttribute;
    public static readonly ARTIFACT_ID: IAttribute;
    public static readonly ROOT_FOLDER: IAttribute;
    public static readonly TENANT: IAttribute;
    public static readonly SERVICE_PRINCIPAL_ID: IAttribute;
    public static readonly SERVICE_PRINCIPAL_KEY: IAttribute;

    public static async validateLakehouseLinkedService(
        linkedService: LakehouseLinkedService,
        context: IValidationContext
    ): Promise<IViolation[]> {
        if (GlobalVariables.isTrident) {
            // MDF: Workspace built-in artifacts use objectId, others use artifactId/workspaceId
            if (GlobalVariables.isTridentMdf && linkedService.isWorkspaceBuiltIn) {
                // Custom validation with proper display name for Lakehouse
                if (!linkedService.objectId) {
                    return [{
                        message: () => format(shellStringResources.RequiredTemplate, shellStringResources.Lakehouse_Fabric_DisplayText),
                        redirectTo: {
                            entity: linkedService.owner as Named,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            attribute: { name: 'mdfArtifact' } as any
                        },
                        entity: linkedService,
                        // Revalidate checks the CURRENT linked service from owner, not the captured one.
                        // This is needed because the linked service is replaced (not modified) when user selects an artifact,
                        // so the old validator's corrected$ would keep checking the old linkedService object.
                        revalidate: () => {
                            const owner = linkedService.owner?.named;
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const currentLS = (owner as any)?.sourceLinkedService || (owner as any)?.sinkLinkedService;
                            return !!currentLS?.objectId;
                        }
                    }];
                }
                return [];
            }
            return validateLinkedServiceProperties(linkedService, [
                LakehouseLinkedService.ARTIFACT_ID,
                LakehouseLinkedService.WORKSPACE_ID
            ]);
        }

        return undefined;
    }

    public authenticationMethod?: string = lakehouseAuthTypes.ServicePrincipalAuthentication;
    public authReferenceMethod?: string = azureDefaultAuthRefTypes.InlineRefTypeAuthentication;

    // input in service principal, has params
    @Metamodel.attribute({
        display: shellStringResources.ModelName_Tenant,
        association: 'TENANT',
        templatable: true
    })
    public tenant: string;

    // input in service principal, has params
    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalID,
        association: 'SERVICE_PRINCIPAL_ID',
        templatable: true
    })
    public servicePrincipalId?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalKey,
        association: 'SERVICE_PRINCIPAL_KEY',
        templatable: true,
        accepts: ['String'],
        constraint: {
            validator: validateAKVLinkedServiceReference
        }
    })
    public servicePrincipalKey?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalCredentialType
    })
    public servicePrincipalCredentialType?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalCert,
        constraint: {
            validator: validateAKVLinkedServiceReference
        }
    })
    public servicePrincipalCert?: AzureKeyVault;

    public override readonly tridentArtifactType: 'Lakehouse' = GlobalVariables.isTrident ? ModelConstants.lakehouseArtifactType : undefined;

    @Metamodel.attribute({
        display: shellStringResources.WorkspaceId,
        association: 'WORKSPACE_ID',
        accepts: ['String'],
        templatable: true
    })
    public workspaceId?: string;

    @Metamodel.attribute({
        display: format(shellStringResources.ObjectIdLabel, shellStringResources.Lakehouse_DisplayText),
        association: 'ARTIFACT_ID',
        accepts: ['String'],
        templatable: true
    })
    public artifactId?: string;

    @Metamodel.attribute({
        display: shellStringResources.RootFolder,
        association: 'ROOT_FOLDER',
        templatable: true,
        constraint: {
            validator: (linkedservice: LakehouseLinkedService): IViolation[] | undefined => {
                if (GlobalVariables.isTrident && !['Files', 'Tables'].includes(linkedservice?.rootFolder)) {
                    return [
                        {
                            message: () =>
                                sanitizeAndFormatString(
                                    shellStringResources.RootFolder_InvalidValue,
                                    linkedservice.rootFolder
                                )
                        }
                    ];
                }
                return undefined;
            }
        }
    })
    public rootFolder?: RootFolderType = GlobalVariables.isTrident ? 'Tables' : undefined;

    @Metamodel.attribute({
        display: shellStringResources.Credential,
        reference: true,
        constraint: {
            validator: (
                linkedservice: LakehouseLinkedService,
                referencedCredential: Credential
            ): IViolation[] | undefined => {
                if (
                    linkedservice &&
                    !linkedservice.fullyByJsonEditor &&
                    ((GlobalVariables.enableSamiSupport &&
                        linkedservice.authenticationMethod === lakehouseAuthTypes.MSIAuthentication) ||
                        linkedservice.authenticationMethod === lakehouseAuthTypes.UserAssignedManagedIdentity ||
                        linkedservice.authenticationMethod === lakehouseAuthTypes.UserAuth ||
                        (linkedservice.authenticationMethod === lakehouseAuthTypes.ServicePrincipalAuthentication &&
                            linkedservice.authReferenceMethod ===
                                azureDefaultAuthRefTypes.CredentialRefTypeAuthentication)) &&
                    !referencedCredential &&
                    !linkedservice.isWorkspaceBuiltIn
                ) {
                    return [{ message: () => shellStringResources.Error_InvalidCredentialReferenced }];
                }
                return undefined;
            }
        }
    })
    public credential?: Credential;

    public fabricEnvironment?: string; // backend use 'Prod' as default value, ci/staging/msit use value "Msit"
    public defaultSchema?: string = !GlobalVariables.isTrident ? 'dbo' : undefined; // new lakehouse supports edit both schema name and table name
    public sqlAnalyticsEndpoint?: string;

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    public reset(): void {
        this.servicePrincipalId = undefined;
        this.servicePrincipalKey = undefined;
        this.credential = undefined;
        this.tenant = undefined;
    }

    public override getDetails(
        dataset: Dataset,
        additionalSkipAttributes?: Set<string>
    ):
        | Array<{
              label?: string;
              value: string | number;
              name: string;
          }>
        | undefined {
        const lakehouseSkipAttributes = additionalSkipAttributes ? additionalSkipAttributes.add('rootFolder') : new Set<string>(['rootFolder']);
        const details = super.getDetails(dataset, lakehouseSkipAttributes) ?? [];
        return [
            ...details,
            {
                label: shellStringResources.RootFolder,
                value: dataset instanceof FormatDatasetV2 ? 'Files' : 'Tables',
                name: 'rootFolder'
            }
        ];
    }
}
